/**
 * #388 — shared copy and date limits for the country reports and analytics.
 *
 * REVENUE_METHODOLOGY describes what apps/brokers/country_reports.py actually
 * computes. The sold statuses it names are SOLD_POLICY_STATUSES in
 * apps/brokers/views.py; backend/tests/test_country_reports_dates_388.py pins
 * that tuple so a change there flags this text.
 */

export const REVENUE_METHODOLOGY =
  'Premium (revenue) is the total premium of sold policies of brokers in your country ' +
  '(payment received, pending issuance, issued, active, lapsed or expired), by policy start date. ' +
  'Drafts, policies awaiting payment, cancelled and matured policies are not counted. ' +
  'Commission is the sum of current commission calculations by policy date; ' +
  'platform revenue is the platform’s share of that commission.';

export const POLICY_COUNT_NOTE =
  'Total policies uses the same count as your dashboard: every non-archived policy you can see, in any status.';

/** The earliest date a report window may start on — same floor as the API. */
export const MIN_REPORT_DATE = '1900-01-01';

/** Today as YYYY-MM-DD in the browser's local time (not UTC). */
export function localToday(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Why a From/To pair cannot be sent, or null when it can. Date inputs always
 * yield '' or a zero-padded YYYY-MM-DD, so string comparison is date order.
 */
export function reportRangeError(
  dateFrom: string,
  dateTo: string,
  today: string = localToday()
): string | null {
  for (const [label, value] of [
    ['From', dateFrom],
    ['To', dateTo],
  ] as const) {
    if (!value) continue;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${label} date is not a valid date.`;
    if (value < MIN_REPORT_DATE)
      return `${label} date is before ${MIN_REPORT_DATE}; check the year.`;
    if (value > today)
      return `${label} date is in the future; reports can only cover dates up to today.`;
  }
  if (dateFrom && dateTo && dateFrom > dateTo) return 'From date must be on or before the To date.';
  return null;
}
